import { useEffect, useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import {
  eligibleCheckDoneForPublicAccountIdAtom,
  isUserIneligibleAtom,
} from "@mirage/eligibility/useEligibilityCheck";
import { getLoginPathWithReturnRedirectURLParam } from "@mirage/service-auth";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import { useIsLoggedIn } from "@mirage/service-auth/useDropboxAuthentication";
import { tagged } from "@mirage/service-logging";
import {
  isUsersFirstLogin,
  markDashUserFirstUseTimestamp,
} from "@mirage/service-onboarding";
import { hasUserCompletedOnboarding } from "@mirage/service-onboarding/hasUserCompletedOnboarding";
import { isRedirectAllowedWithUrl } from "@mirage/shared/urls";
import type { AppRoute } from "@mirage/webapp/routeTypes";
import { PageType } from "@mirage/webapp/routeTypes";
import { useAtomValue } from "jotai";

import { isDashEmbeddedWeb } from "@mirage/shared/utils/environment-detection";
import { ROUTES } from "../Routes";
import { REDIRECT_TO_PATH_URL_PARAM } from "../shared/constants";

const logger = tagged("useAuthCheck");

export function useAuthCheck(route: AppRoute) {
  const navigate = useNavigate();
  const loggedIn = useIsLoggedIn();
  const account = useDropboxAccount();
  const accountId = account?.account_id;
  const eligibleCheckDoneForPublicAccountId = useAtomValue(
    eligibleCheckDoneForPublicAccountIdAtom,
  );
  const isUserIneligible = useAtomValue(isUserIneligibleAtom);
  const isUserCheckedAndEligible =
    eligibleCheckDoneForPublicAccountId !== undefined &&
    eligibleCheckDoneForPublicAccountId === account?.public_account_id &&
    !isUserIneligible;
  const [pendingEligibility, setPendingEligibility] = useState(true);

  // Extract properties from route because the `route` object changes on every
  // render, and should not be used as a useEffect dependency.
  const { requiresAuthentication, pageType } = route;

  useEffect(() => {
    const doPostLoginLogout = async () => {
      // If the user is not allowed to use the app, navigate to not found.
      if (loggedIn) {
        const pending = await onLoggedIn(
          pageType,
          navigate,
          isUserCheckedAndEligible,
          eligibleCheckDoneForPublicAccountId,
          accountId,
        );
        setPendingEligibility(pending);
      } else {
        // No need to await for completion.
        void onLoggedOut(pageType, navigate, requiresAuthentication);
        setPendingEligibility(false);
      }
    };
    doPostLoginLogout();
  }, [
    loggedIn,
    navigate,
    requiresAuthentication,
    pageType,
    isUserCheckedAndEligible,
    eligibleCheckDoneForPublicAccountId,
    accountId,
  ]);

  return { loggedIn, pendingEligibility };
}

// exported for testing
export async function onLoggedIn(
  pageType: PageType,
  navigate: NavigateFunction,
  isUserCheckedAndEligible: boolean,
  eligibleCheckDoneForPublicAccountId: string | undefined,
  accountId: string | undefined,
) {
  logger.debug("onLoggedIn() - pageType", pageType);
  // FSS will be appending public, api-compatible form of the User ID ("dbid:xxxx") to the dash URL e.g /embed/chat?user_id=dbid:123456
  // We need to check if the user ID from the URL matches the account ID.
  // If it does not match, we log the user out which notifies FSS.
  if (pageType !== PageType.LOGOUT) {
    const query = new URLSearchParams(window.location.search);
    const userIdParam = query.get("user_id");
    if (userIdParam && accountId && userIdParam !== accountId) {
      logger.debug(
        "onLoggedIn() - user_id from URL does not match account ID, logging out",
      );
      navigate("/logout");
      return false;
    }
  }
  // If the user is logged in and lands on the login page, navigate to main page.
  if (pageType === PageType.LOGIN) {
    logger.debug("onLoggedIn() - navigate to main page (pageType is LOGIN)");

    // Check for redirectToPathEncoded parameter
    const query = new URLSearchParams(window.location.search);
    const redirectToPathEncoded = query.get(REDIRECT_TO_PATH_URL_PARAM);

    let redirectPath = `${ROUTES.HOME.path}${window.location.search}`;

    if (redirectToPathEncoded) {
      try {
        const decodedPath = decodeURIComponent(redirectToPathEncoded);
        if (isRedirectAllowedWithUrl(decodedPath)) {
          redirectPath = decodedPath;
          logger.debug(
            "onLoggedIn() - redirecting to encoded path",
            decodedPath,
          );
        } else {
          logger.warn("onLoggedIn() - redirect path not allowed", decodedPath);
        }
      } catch (e) {
        logger.warn("onLoggedIn() - error decoding redirect path", e);
      }
    }

    navigate(redirectPath);
    return false;
  }

  if (pageType === PageType.LOGOUT) {
    return false;
  }

  // Eligible users on non-special Page Types
  const firstTimeUser = await isUsersFirstLogin(); // cached value

  logger.debug(
    "onLoggedIn() - is first time user =",
    firstTimeUser ? "true" : "false",
  );

  if (firstTimeUser && !eligibleCheckDoneForPublicAccountId) {
    return true;
  }

  if (isUserCheckedAndEligible && firstTimeUser) {
    if (pageType !== PageType.SETUP) {
      if (
        (await hasUserCompletedOnboarding()) ||
        // Embedded dash does not have onboarding, so we mark first use timestamp without onboarding
        isDashEmbeddedWeb()
      ) {
        await markDashUserFirstUseTimestamp();
      }
    }
  }
  return false;
}

async function onLoggedOut(
  pageType: PageType,
  navigate: NavigateFunction,
  requiresAuthentication: boolean,
) {
  logger.debug("onLoggedOut() - pageType", pageType);
  // User is not logged in.
  if (!requiresAuthentication) {
    logger.debug("onLoggedOut() - authentication not required");
    // Allow page to render.
    return;
  }

  if (pageType !== PageType.LOGIN) {
    const loginPath = await getLoginPathWithReturnRedirectURLParam();
    logger.debug("onLoggedOut() - navigating to new path", loginPath);
    navigate(loginPath);
  }
}
